import { ChangeDetectionStrategy, Component, computed, DOCUMENT, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  AtFooterComponent,
  AtSpinnerComponent,
  ConfigurableAuthService,
  FooterLink,
  matchMediaQuery,
} from '@anothertrip/anothertrip-ds';
import { filter, map, shareReplay, startWith } from 'rxjs';

import { HeaderWrapperComponent } from './components/header-wrapper/header-wrapper.component';
import { MapJobNoticesComponent } from './components/map-job-notices/map-job-notices.component';
import { CookieSettingsLinksDirective } from './directives/cookie-settings-links/cookie-settings-links.directive';
import { DASHBOARD_SIDEBAR_QUERY } from './layouts/dashboard/dashboard-sidebar';
import { isPageNotFound } from './pages/page-not-found/page-not-found.util';
import { COOKIE_SETTINGS_HREF, CookieConsentService } from './services/cookie-consent/cookie-consent.service';
import { MapJobsService } from './services/map-jobs.service';

export type PageType =
  | 'map'
  | 'dashboard'
  | 'get-started'
  | 'default'
  | 'subscription-processing'
  | 'subscription-complete';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderWrapperComponent,
    AtFooterComponent,
    AtSpinnerComponent,
    MapJobNoticesComponent,
    CookieSettingsLinksDirective,
  ],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly router = inject(Router);
  private readonly authService = inject(ConfigurableAuthService);
  private readonly body = inject(DOCUMENT).body;
  private readonly mapJobs = inject(MapJobsService);
  private readonly cookieConsent = inject(CookieConsentService);

  protected readonly pageType = toSignal<PageType>(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) =>
        isPageNotFound(this.router.routerState.snapshot) ? 'default' : this.getPageTypeFromUrl(e.urlAfterRedirects)
      ),
      startWith(this.getPageTypeFromUrl(window.location.href)),
      shareReplay({ bufferSize: 1, refCount: true })
    )
  );

  protected readonly isUIVisible = computed<boolean>(
    () =>
      this.pageType() !== 'map' &&
      this.pageType() !== 'subscription-processing' &&
      this.pageType() !== 'subscription-complete'
  );

  private readonly isDesktop = matchMediaQuery(DASHBOARD_SIDEBAR_QUERY);

  /** The sidebar is standing on desktop dashboard routes: the footer must start where it ends. */
  protected readonly isSidebarVisible = computed<boolean>(() => this.pageType() === 'dashboard' && this.isDesktop());

  /**
   * Hide the top header when the dashboard sidebar is visible. The sidebar carries the brand and
   * profile on desktop, so a second chrome strip above the page is redundant.
   */
  protected readonly isHeaderVisible = computed<boolean>(() => this.isUIVisible() && !this.isSidebarVisible());

  protected readonly footerLinks: FooterLink[] = [
    { label: 'Blog', href: 'https://content.anothertrip.com/blog' },
    { label: 'Terms of use', routerLink: '/terms-of-use' },
    { label: 'Privacy policy', routerLink: '/privacy-policy' },
    // Opens the consent kit's dialog rather than going anywhere: `appCookieSettingsLinks` on the footer
    // answers the click (AT-2748).
    { label: 'Cookie settings', href: COOKIE_SETTINGS_HREF },
  ];

  protected readonly isLoggingOut = this.authService.isLoggingOut;

  constructor() {
    effect(() => this.handleLogout(this.isLoggingOut()));
    // Before the router's first navigation, which can redirect and lose the hash.
    this.cookieConsent.openIfLinked();
  }

  private getPageTypeFromUrl(url: string): PageType {
    if (url.startsWith('/') && url.length === 1) return 'get-started';
    // All of these paths load inside the DashboardLayout, where the fixed sidebar is the chrome.
    // Treating them as 'dashboard' keeps the top header hidden on desktop and lets the footer start
    // after the sidebar.
    if (
      url.includes('/dashboard') ||
      url.includes('/account') ||
      url.includes('/profile') ||
      url.includes('/billing') ||
      url.includes('/collaboration') ||
      url.includes('/marketplace') ||
      url.includes('/tax-audit') ||
      // My campaigns only — the public `/campaigns/:id` share alias is not a dashboard page.
      url.includes('/campaigns/mine')
    ) {
      return 'dashboard';
    }
    if (url.includes('/map')) return 'map';
    if (url.includes('/get-started')) return 'get-started';
    if (url.includes('/subscription/processing')) return 'subscription-processing';
    if (url.includes('/subscription/complete')) return 'subscription-complete';
    return 'default';
  }

  private handleLogout(isLoggingOut: boolean): void {
    if (isLoggingOut) {
      // The next account must not be told about, or keep polling for, the last one's maps (AT-2685).
      this.mapJobs.cancelAll();
      this.body.style.overflow = 'hidden';
      this.body.dataset['logoutScrollLock'] = 'true';
    } else {
      this.body.style.overflow = '';
      delete this.body.dataset['logoutScrollLock'];
    }
  }
}
